import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'idsk-react-components';
import { getTitle, setTitle } from 'slovlex-idsk-react-components';
import { useSuccessMessageContext } from '../../context/SuccessMessageContext';
import { useClient, useClientStatusContext } from '../../context/ClientStatusContext';
import PrispevokVestnika from '../../model/PrispevokVestnika';
import useFieldErrorMessages from '../../hooks/useFieldErrorMessages';
import { validatePrispevokVestnika } from '../../client/prispevokVestnikaAddClient';
import { SafeParameterSetter } from '../../model/types/SafeParameterSetter';
import PrispevokVestnikaStepEnum from '../../model/PrispevokVestnikaStepEnum';
import routes from '../../routing/routes';
import BasicDataStep1 from './stepPages/BasicDataStep1/BasicDataStep1Update';
import DatesStep2 from './stepPages/DatesStep2';
import RelatedDataStep3 from './stepPages/RelatedDataStep3';
import AttachmentsStep4 from './stepPages/AttachmentsStep4';
import MainAttachmentStep5 from './stepPages/MainAttachmentStep5';
import CheckDataStep6 from './stepPages/CheckDataStep6';
import { updatePrispevokVestnika } from '../../client/prispevokVestnikaUpdateClient';
import './stepPages/stepPages.scss';
import scrollToTop from '../../function/scrollToTop';

interface Props {
  vestnikDetail: PrispevokVestnika;
}

const PrispevokVestnikaUpdate = ({ vestnikDetail }: Props): ReactElement => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    clearStatus,
  } = useClientStatusContext();

  const [prispevokVestnika, setPrispevokVestnika] = useState<PrispevokVestnika>(
    {
      ...vestnikDetail,
      activeStep: PrispevokVestnikaStepEnum.BASIC_DATA_1,
    },
  );

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
    clearArrayFieldErrorMessage,
  } = useFieldErrorMessages();
  const {
    wrapClient: validatePrispevokVestnikaClient,
    status: validatePrispevokVestnikaStatus,
  } = useClient(validatePrispevokVestnika);
  const {
    wrapClient: updatePrispevokVestnikaClient,
    status: updatePrispevokVestnikaStatus,
  } = useClient(updatePrispevokVestnika);

  const updateField: SafeParameterSetter<PrispevokVestnika> = (fieldName, value) => {
    setPrispevokVestnika(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const onChange = async (
    nextStep: PrispevokVestnikaStepEnum,
  ): Promise<void> => {
    scrollToTop();
    if (prispevokVestnika.activeStep === PrispevokVestnikaStepEnum.CHECK_DATA_6) {
      updateField('activeStep', nextStep);
    } else if (prispevokVestnika.activeStep !== nextStep) {
      await validatePrispevokVestnikaClient(prispevokVestnika);
      updateField('activeStep', nextStep);
    }
  };

  const handleSubmit = async (e, nextStep): Promise<void> => {
    e.preventDefault();
    switch (prispevokVestnika.activeStep) {
      case PrispevokVestnikaStepEnum.BASIC_DATA_1:
      case PrispevokVestnikaStepEnum.DATES_2:
      case PrispevokVestnikaStepEnum.RELATED_DATA_3:
      case PrispevokVestnikaStepEnum.ATTACHMENTS_4:
      case PrispevokVestnikaStepEnum.MAIN_ATTACHMENT_5:
        await validatePrispevokVestnikaClient(prispevokVestnika);
        updateField('activeStep', nextStep);
        scrollToTop();
        return;
      case PrispevokVestnikaStepEnum.CHECK_DATA_6:
        await updatePrispevokVestnikaClient(prispevokVestnika);
        navigate(routes.prispevokVestnika.detail.withParams(vestnikDetail.uuid));
        displaySuccess('Príspevok vestníka bol úspešne odoslaný do úložiska.');
        scrollToTop();
        return;
      default:
        updateField('activeStep', nextStep);
        scrollToTop();
    }
  };

  const renderActiveStepPage = (): ReactElement => {
    switch (prispevokVestnika.activeStep) {
      case PrispevokVestnikaStepEnum.DATES_2:
        return (
          <DatesStep2
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
            isUpdate
          />
        );
      case PrispevokVestnikaStepEnum.RELATED_DATA_3:
        return (
          <RelatedDataStep3
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
            isUpdate
          />
        );
      case PrispevokVestnikaStepEnum.ATTACHMENTS_4:
        return (
          <AttachmentsStep4
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
            clearArrayFieldErrorMessage={clearArrayFieldErrorMessage}
            clearFieldErrorMessage={clearFieldErrorMessage}
            isUpdate
          />
        );
      case PrispevokVestnikaStepEnum.MAIN_ATTACHMENT_5:
        return (
          <MainAttachmentStep5
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
            isUpdate
          />
        );
      case PrispevokVestnikaStepEnum.CHECK_DATA_6:
        return (
          <CheckDataStep6
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={updatePrispevokVestnikaStatus.loading}
            isUpdate
          />
        );
      default:
        return (
          <BasicDataStep1
            onChange={onChange}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
    }
  };

  useEffect(() => {
    setTitle(getTitle());
  }, [prispevokVestnika.activeStep]);

  return (
    <Row>
      <Col widthDesktop="three-quarters">
        {renderActiveStepPage()}
      </Col>
    </Row>
  );
};

export default PrispevokVestnikaUpdate;
